import { useState, useEffect, useMemo } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { X, Loader2, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { nextInvoiceNumber } from '@/lib/invoiceHelpers';

// Combines several existing per-load Unpaid invoice drafts (every load
// auto-gets one at delivery) into ONE invoice actually sent to the
// customer -- common when a customer had several loads in a billing
// period and expects one bill, not one per load. The originals get
// voided; invoice_loads links the new combined invoice back to each
// original load so the Loads table's "has an invoice" indicator still
// resolves correctly against it.
export default function BatchInvoiceModal({ onClose, onCreated }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    client.entities.Invoice.filter({ status: 'Unpaid' }, '-invoiced_date', 500)
      .then(setInvoices)
      .finally(() => setLoading(false));
  }, []);

  const customers = useMemo(() => {
    const counts = new Map();
    for (const inv of invoices) {
      if (!inv.customer_name) continue;
      counts.set(inv.customer_name, (counts.get(inv.customer_name) || 0) + 1);
    }
    return [...counts.entries()].filter(([, n]) => n >= 2).sort((a, b) => a[0].localeCompare(b[0]));
  }, [invoices]);

  const customerInvoices = useMemo(
    () => invoices.filter(i => i.customer_name === customer),
    [invoices, customer]
  );

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectedInvoices = customerInvoices.filter(i => selected.has(i.id));
  const total = selectedInvoices.reduce((sum, i) => sum + (i.total_amount ?? i.balance_due ?? 0), 0);

  const handleSave = async () => {
    if (selectedInvoices.length < 2) { toast.error('Select at least 2 invoices to combine.'); return; }
    setSaving(true);
    try {
      const allInvoices = await client.entities.Invoice.list('-created_date', 1000);
      const invoiceNumber = nextInvoiceNumber(allInvoices);
      const loadNumbers = selectedInvoices.map(i => i.load_number).filter(Boolean);
      const combined = await client.entities.Invoice.create({
        invoice_number: invoiceNumber,
        customer_name: customer,
        customer_email: selectedInvoices.find(i => i.customer_email)?.customer_email || undefined,
        total_amount: total,
        balance_due: total,
        invoiced_date: new Date().toISOString().split('T')[0],
        status: 'Unpaid',
        notes: loadNumbers.length ? `Combined invoice for Load${loadNumbers.length !== 1 ? 's' : ''} #${loadNumbers.join(', #')}` : undefined,
      });

      await Promise.all(selectedInvoices.map(async (inv) => {
        if (inv.load_id) {
          await client.entities.InvoiceLoad.create({ invoice_id: combined.id, load_id: inv.load_id });
        }
        await client.entities.Invoice.update(inv.id, { status: 'Void' });
      }));

      toast.success(`Combined ${selectedInvoices.length} invoices into ${invoiceNumber}.`);
      onCreated();
      onClose();
    } catch (e) {
      toast.error('Failed to combine invoices.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg my-8 p-6 space-y-4 z-10 bg-background border border-border rounded-2xl shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-primary" />
            <h2 className="font-space font-semibold text-foreground text-lg">Batch Invoice</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-muted-foreground">
          Combine several existing draft invoices for the same customer into one invoice. The originals will be voided.
        </p>

        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : customers.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No customer has 2+ Unpaid invoices to combine right now.</p>
        ) : (
          <>
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">Customer</label>
              <select
                value={customer}
                onChange={e => { setCustomer(e.target.value); setSelected(new Set()); }}
                className="w-full px-3 py-2 rounded-lg bg-muted border border-border text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
              >
                <option value="" className="bg-background text-muted-foreground">— Select a customer —</option>
                {customers.map(([name, count]) => (
                  <option key={name} value={name} className="bg-background">{name} ({count} unpaid)</option>
                ))}
              </select>
            </div>

            {customer && (
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {customerInvoices.map(inv => (
                  <button
                    key={inv.id}
                    onClick={() => toggle(inv.id)}
                    className={cn(
                      "w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm text-left transition-colors",
                      selected.has(inv.id) ? "border-primary/40 bg-primary/10" : "border-border bg-muted/20 hover:bg-muted/40"
                    )}
                  >
                    <span className="text-foreground">
                      {inv.invoice_number}{inv.load_number ? ` — Load #${inv.load_number}` : ''}
                    </span>
                    <span className="font-semibold text-foreground">${(inv.total_amount ?? inv.balance_due ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </button>
                ))}
              </div>
            )}

            {selectedInvoices.length > 0 && (
              <div className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-primary/10 border border-primary/20 text-sm">
                <span className="font-semibold text-foreground">{selectedInvoices.length} selected</span>
                <span className="font-bold text-primary">${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
            )}
          </>
        )}

        <div className="flex gap-3 pt-1">
          <Button onClick={handleSave} disabled={saving || selectedInvoices.length < 2} className="flex-1 bg-primary text-primary-foreground">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Combining...</> : `Combine Into One Invoice`}
          </Button>
          <Button onClick={onClose} variant="outline" className="bg-background border-border hover:bg-muted">Cancel</Button>
        </div>
      </div>
    </div>
  );
}
